// C-transport / C-sources shapes and the phone's motion between messages, kept free of the store so they test bare.

/** Publish period while anything moves (≤10 Hz). */
export const TICK_MS = 100

/** The preset the transport goes to next: the fade's destination, else the playlist's next (playing or not), else -1. */
export const upNextIndex = ({ current_preset_index: cur, next_preset_index: next, loop }, count) => {
	if (next >= 0) return next
	if (count < 2) return -1
	const i = Math.max(cur, 0) + 1
	return i < count ? i : loop ? 0 : -1
}

/** Signed change per second from `a` at `ta` to `b` at `tb`; `wrap` takes the short way round a 0…1 cycle. */
export const rateOf = (a, b, dtMs, wrap = false) => {
	if (!(dtMs > 0) || !Number.isFinite(a) || !Number.isFinite(b)) return 0
	let d = b - a
	if (wrap) d -= Math.round(d)
	return (d * 1000) / dtMs
}

const round = (v, n = 1e4) => Math.round(v * n) / n

/** A playhead that goes round a 0…1 cycle: a repeating time/BPM loop. An audio drive's position is a level, a one-shot or yoyo clamps. */
export const cycles = def => def?.mode === 'loop' && (def.drive === 'time' || def.drive === 'bpm')

export const buildTransport = ({ playback, playlist, child, phases, now, prev, prevAt, wraps = () => true }) => {
	const dt = prev ? now - prevAt : 0
	// A fade with no destination preset is not the transport's: a source switch's, or a landed one still installed.
	const kind = child?.kind === 'fade' && !(playback.next_preset_index >= 0) ? null : (child?.kind ?? null)
	const progress = kind ? round(child.progress) : 0
	const sameChild = prev && prev.kind === kind && prev.child === child?.index
	// A child at either end has stopped. A landed manual fade stays installed at 1 while the ids already name the next pair: rated, the phone would draw it as a fade from the new current to the playlist's next.
	const ended = progress >= 1 || progress <= 0
	// A scrubbed or parked fade moves only by hand: its rate is the finger's, which a phone would coast past the release.
	const held = !!playback.transition_paused
	const next = upNextIndex(playback, playlist.length)
	const out = {}
	for (const [path, pos] of Object.entries(phases ?? {})) {
		if (!Number.isFinite(pos)) continue
		const was = prev?.phases?.[path]?.[0]
		out[path] = [round(pos), was === undefined ? 0 : round(rateOf(was, pos, dt, wraps(path)), 1e3)]
	}
	return {
		current_id: playlist[playback.current_preset_index]?.id ?? null,
		next_id: playlist[next]?.id ?? null,
		kind,
		child: kind ? child.index : null,
		progress,
		rate: kind && sameChild && !ended && !held ? round(rateOf(prev.progress, progress, dt), 1e3) : 0,
		playing: !!playback.playing,
		held,
		phases: out,
	}
}

/** Anything the phone draws as moving: it needs the next tick even with no other change. */
export const isMoving = tr => !!tr && (tr.rate !== 0 || Object.values(tr.phases).some(([, r]) => r !== 0))

/** Everything but the motion: a still transport is sent only when this changes. */
export const transportKey = tr =>
	JSON.stringify([
		tr.current_id,
		tr.next_id,
		tr.kind,
		tr.child,
		tr.playing,
		tr.held,
		tr.rate === 0 && tr.progress,
		// A held playhead's place: an in/out edit can move it.
		Object.keys(tr.phases)
			.sort()
			.map(p => (tr.phases[p][1] === 0 ? [p, tr.phases[p][0]] : p)),
	])

// A silent runner stops the coasting here rather than spinning the readouts on.
export const MAX_COAST_MS = 1000

const clamp01 = v => Math.min(1, Math.max(0, v))

/** `tr` (received at local `at`) moved on to local `now`; a phase whose `wraps(path)` is false (yoyo, one-shot) clamps instead of wrapping. */
export const transportAt = (tr, at, now, wraps = () => true) => {
	if (!tr) return tr
	const s = Math.min(MAX_COAST_MS, Math.max(0, now - at)) / 1000
	const phases = {}
	for (const [path, [pos, rate]] of Object.entries(tr.phases)) {
		const p = pos + rate * s
		phases[path] = wraps(path) ? ((p % 1) + 1) % 1 : clamp01(p)
	}
	return { ...tr, progress: clamp01(tr.progress + tr.rate * s), phases }
}

/** `device`: this machine's camera, the item a `camera` fill shows. */
export const buildSources = ({ items, device, manager }) => ({
	device,
	items: items
		.filter(i => i.id !== 'video_file')
		.map(i => {
			let state = i.state ?? 'ready'
			if (i.type === 'camera' || i.type === 'screen') {
				const s = manager?.sources?.[i.id]
				state = s?.needsGesture ? 'needs_gesture' : (s?.state ?? 'off')
			}
			return { id: i.id, type: i.type, title: i.name, state }
		}),
})
